import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User, ConciergeBell, BedDouble, CarFront, ShieldCheck, LayoutDashboard, RotateCcw, Loader2 } from 'lucide-react';
import { clearGuidedTourSeen } from './shared/GuidedTour';
import LanguageToggle from './LanguageToggle';
import { useTranslation } from 'react-i18next';
import {
    hotelDemoCards, refreshHotelDemoCards, routeCardId, isHotelDemoCard,
    isHotelDemoCommandCenter, HOTEL_DEMO_ORG_ID,
} from '../lib/hotelDemo';

// Live role switcher for Holiday Inn HOTEL demo. Same structure as the
// automotive switcher (bottom-center blurred pill, direct jump, same tab,
// RELATIVE paths only) — but a separate component: AutoDemoSwitcher is not
// touched.
//
// Visibility is derived from the PAGE (demo card id in the path / demo org on
// the Command Center), so it does not matter HOW the demo was entered: card
// tap, portal link, fresh tab or the launcher. The old per-tab flag is kept as
// one more way in, never as the only one. Real hotel clients never match.
export function shouldShowHotelDemoSwitcher(pathname, search, cards = hotelDemoCards()) {
    if (typeof window === 'undefined') return false;
    let hotelDemo = false;
    try { hotelDemo = sessionStorage.getItem('yulontap_hotel_demo') === '1'; } catch (_e) { /* ignore */ }
    const cardId = routeCardId(pathname);
    if (cardId) return isHotelDemoCard(cardId, cards) || hotelDemo;
    if (pathname === '/command-center') return isHotelDemoCommandCenter(search) || hotelDemo;
    return false;
}

export default function HotelDemoSwitcher() {
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [cards, setCards] = useState(hotelDemoCards);
    const [resetting, setResetting] = useState(false);

    // The demo card list is cached in localStorage, so a fresh tab renders the
    // pill on the FIRST paint; this only keeps the list honest.
    useEffect(() => {
        let alive = true;
        refreshHotelDemoCards().then((c) => { if (alive) setCards(c); }).catch(() => { /* cached list stands */ });
        return () => { alive = false; };
    }, []);

    const visible = shouldShowHotelDemoSwitcher(pathname, search, cards);

    // The pill is fixed to the bottom, so without this the last row of a
    // scrolling page (Command Center overview, guest request list) sits under it.
    useEffect(() => {
        if (!visible) return;
        const prev = document.body.style.paddingBottom;
        document.body.style.paddingBottom = '76px';
        return () => { document.body.style.paddingBottom = prev; };
    }, [visible]);

    if (!visible) return null;

    // Same behaviour as the automotive pill's reset: confirm, then call the
    // demo's OWN reset endpoint. That endpoint is scoped to the hotel demo org
    // and closes open requests through the normal lifecycle (deletes nothing),
    // so no real organization can be touched. It is a plain fetch with NO
    // credentials, so a real admin's session is not involved at all.
    const doReset = async () => {
        if (resetting) return;
        if (!window.confirm(t('hotelDemo.resetConfirm',
            'Reset the hotel demo? This closes every request left open by an earlier run so each department starts green. Nothing is deleted, and only the demo hotel is affected.'))) return;
        setResetting(true);
        try {
            const res = await fetch(`${(typeof window !== 'undefined' ? window.location.origin : '')}/api/demo-hotel/reset`, { method: 'POST' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            // Reset is the ONE thing that starts a new guest: drop this
            // browser's demo stay (the DEMO org's key only — a real guest's
            // stay for another hotel in the same browser is never touched) and
            // the staff session, then land on the guest entry so the presenter
            // sees registration again.
            try {
                localStorage.removeItem(`yulontap_stay:${HOTEL_DEMO_ORG_ID}`);
                cards.forEach((c) => localStorage.removeItem(`yulontap_assistant:${c}`));
                sessionStorage.removeItem('yulontap_hotel_demo_staff');
                sessionStorage.removeItem('yulontap_hotel_demo_offer_guest');
                // A fresh demo should feel like a first visit: replay the guided tour.
                clearGuidedTourSeen();
            } catch (_e) { /* ignore */ }
            window.location.replace('/demo/hotel/guest');
        } catch (_e) { setResetting(false); }
    };

    let active = null;
    if (pathname === '/command-center') active = 'manager';
    else if (pathname.startsWith('/hotel/')) active = 'guest';
    else if (pathname.startsWith('/assistant/staff/')) {
        try {
            const raw = sessionStorage.getItem('yulontap_hotel_demo_staff_active')
                || sessionStorage.getItem('yulontap_hotel_demo_staff');
            active = raw ? (JSON.parse(raw).department || null) : null;
        } catch { active = null; }
    } else if (pathname.startsWith('/assistant/')) active = 'guest';

    const items = [
        { key: 'guest', path: '/demo/hotel/guest', icon: <User className="w-4 h-4" />, label: t('hotelDemo.role.guest', 'Guest'), grad: 'from-amber-500 to-yellow-600', ring: 'ring-amber-300' },
        { key: 'concierge', path: '/demo/hotel/concierge', icon: <ConciergeBell className="w-4 h-4" />, label: t('hotelDemo.role.concierge', 'Concierge'), grad: 'from-emerald-500 to-green-600', ring: 'ring-emerald-300' },
        { key: 'housekeeping', path: '/demo/hotel/housekeeping', icon: <BedDouble className="w-4 h-4" />, label: t('hotelDemo.role.housekeeping', 'Housekeeping'), grad: 'from-teal-500 to-cyan-600', ring: 'ring-teal-300' },
        { key: 'valet', path: '/demo/hotel/valet', icon: <CarFront className="w-4 h-4" />, label: t('hotelDemo.role.valet', 'Valet'), grad: 'from-indigo-500 to-violet-600', ring: 'ring-indigo-300' },
        { key: 'security', path: '/demo/hotel/security', icon: <ShieldCheck className="w-4 h-4" />, label: t('hotelDemo.role.security', 'Security'), grad: 'from-rose-500 to-red-600', ring: 'ring-rose-300' },
        { key: 'manager', path: '/demo/hotel/manager', icon: <LayoutDashboard className="w-4 h-4" />, label: t('hotelDemo.role.manager', 'Manager'), grad: 'from-sky-500 to-blue-600', ring: 'ring-sky-300' },
    ];

    return (
        <div
            role="group"
            aria-label={t('hotelDemo.switchAria', 'Switch hotel demo role')}
            data-testid="hotel-demo-switcher"
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-nowrap items-center gap-1.5 rounded-full border border-white/15 bg-black/70 backdrop-blur px-1.5 py-1.5 shadow-xl max-w-[calc(100vw-1rem)] overflow-x-auto no-scrollbar">
            <span className="px-2 text-[10px] font-semibold uppercase tracking-widest text-white/70 hidden sm:inline">
                {t('hotelDemo.liveBadge', 'Live Demo')}
            </span>
            {items.map((it) => {
                const isActive = active === it.key;
                return (
                    <button
                        key={it.key}
                        onClick={() => navigate(it.path)}
                        aria-label={it.label}
                        aria-current={isActive ? 'true' : undefined}
                        title={isActive ? t('hotelDemo.currentView', 'Current view') : it.label}
                        data-testid={`hotel-demo-switch-${it.key}`}
                        className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white border-0 bg-gradient-to-r ${it.grad} transition-transform duration-200 hover:opacity-90 ${isActive ? `ring-2 ${it.ring} ring-offset-2 ring-offset-black scale-105 shadow-lg` : 'opacity-90 hover:scale-105'}`}>
                        {it.icon}<span className="hidden sm:inline">{it.label}</span>
                    </button>
                );
            })}
            <span className="w-px h-5 shrink-0 bg-white/15 mx-0.5" aria-hidden="true" />
            <div className="shrink-0 w-[150px]" data-testid="demo-language-picker">
                <LanguageToggle variant="compact" surface="demo" />
            </div>
            <span className="w-px h-5 shrink-0 bg-white/15 mx-0.5" aria-hidden="true" />
            <button
                onClick={doReset}
                disabled={resetting}
                aria-label={t('hotelDemo.resetPillAria', 'Reset the hotel demo')}
                title={t('hotelDemo.resetDemo', 'Reset demo state')}
                data-testid="hotel-demo-reset-pill"
                className="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-slate-200 border border-white/15 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50">
                {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                <span className="hidden xl:inline">{t('hotelDemo.resetDemo', 'Reset demo state')}</span>
            </button>
        </div>
    );
}
